import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CARRIER_NAMES, CARRIER_SERVICES, type CarrierId, type ParcelShopDto, type PrinterDto, type ShipmentDto, type ShippingPlanDto } from '@thebb/shared';
import { get, post, put } from '../api';
import { useAuth } from '../auth';
import { Badge, Modal, Spinner, fmtTime } from './ui';

export const shipmentStatusLabel: Record<string, string> = { draft: 'Kladde', labelled: 'Label klar', handed_over: 'Afleveret', in_transit: 'Undervejs', delivered: 'Leveret', failed: 'Fejlet', cancelled: 'Annulleret' };
export const shipmentTone = (s: string) => (s === 'delivered' ? 'green' : s === 'failed' || s === 'cancelled' ? 'red' : s === 'labelled' ? 'blue' : 'amber') as 'green' | 'red' | 'blue' | 'amber';
export const modeLabel: Record<string, string> = { dry_run: 'testkørsel', test: 'testmiljø', live: 'live' };
const kg = (g: number) => `${(g / 1000).toFixed(1)} kg`;
const read = (k: string) => { try { return localStorage.getItem(k) ?? ''; } catch { return ''; } };

/** The station's label printer, remembered per browser next to the laser printer. */
export function useLabelPrinter() {
  const [id, setState] = useState(() => read('station.labelPrinterId'));
  const set = (v: string) => { setState(v); try { localStorage.setItem('station.labelPrinterId', v); } catch { /* ignore */ } };
  return [id, set] as const;
}

export function LabelPrinterSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const printers = useQuery({ queryKey: ['printers'], queryFn: () => get<PrinterDto[]>('/printing/printers') });
  return (
    <select className="input w-56" value={value} onChange={(e) => onChange(e.target.value)} title="Printer til fragtlabels">
      <option value="">Labels: ingen printer (PDF)</option>
      {printers.data?.filter((p) => p.enabled).map((p) => <option key={p.id} value={p.id}>Labels: {p.displayName}</option>)}
    </select>
  );
}

export function ShipmentRow({ s, onChanged, onError, laserPrinterId, labelPrinterId }: { s: ShipmentDto; onChanged: () => void; onError: (e: unknown) => void; laserPrinterId?: string; labelPrinterId?: string }) {
  const { can } = useAuth();
  const cancel = useMutation({ mutationFn: () => post(`/shipping/shipments/${s.id}/cancel`), onSuccess: onChanged, onError });
  const reprint = useMutation({ mutationFn: () => post(`/shipping/shipments/${s.id}/reprint`, { labelPrinterId: labelPrinterId || null, documentPrinterId: laserPrinterId || null }), onSuccess: onChanged, onError });
  const open = (id: string) => window.open(`/api/documents/${id}/download`, '_blank');
  return (
    <tr>
      <td className="whitespace-nowrap font-medium">{s.number}</td>
      <td>{CARRIER_NAMES[s.carrier]} <span className="text-muted">· {s.service}</span>{s.mode !== 'live' && <span className="ml-1"><Badge tone="amber">{modeLabel[s.mode]}</Badge></span>}</td>
      <td className="whitespace-nowrap tabular-nums">{s.packageCount} · {kg(s.weightG)}</td>
      <td className="tabular-nums">
        {s.trackingUrl ? <a href={s.trackingUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">{s.consignmentNumber}</a> : s.consignmentNumber}
        {s.trackingNumbers.length > 1 && <div className="text-xs text-muted">{s.trackingNumbers.join(', ')}</div>}
        {s.error && <div className="text-xs text-red-700">{s.error}</div>}
      </td>
      <td className="whitespace-nowrap"><Badge tone={shipmentTone(s.status)}>{shipmentStatusLabel[s.status]}</Badge>{s.priceMinor != null && <div className="text-xs text-muted">{(s.priceMinor / 100).toFixed(2)} kr</div>}</td>
      <td className="whitespace-nowrap text-xs text-muted">{s.bookedBy}{s.bookedAt ? ` · ${fmtTime(s.bookedAt)}` : ''}</td>
      <td className="whitespace-nowrap text-right">
        {s.labelDocumentIds.map((d, i) => <button key={d} className="btn" onClick={() => open(d)}>Label{s.labelDocumentIds.length > 1 ? ` ${i + 1}` : ''}</button>)}{' '}
        {s.customsDocumentId && <button className="btn" onClick={() => open(s.customsDocumentId!)}>Toldfaktura</button>}{' '}
        {['labelled', 'handed_over'].includes(s.status) && can('shipping.book') && (labelPrinterId || laserPrinterId) && <button className="btn" disabled={reprint.isPending} onClick={() => reprint.mutate()}>Print igen</button>}{' '}
        {['labelled', 'failed', 'draft'].includes(s.status) && can('shipping.cancel') && <button className="btn" disabled={cancel.isPending} onClick={() => confirm(`Annullér ${s.number}${s.consignmentNumber ? ` (${s.consignmentNumber})` : ''}? Labelen må ikke bruges bagefter.`) && cancel.mutate()}>Annullér</button>}
      </td>
    </tr>
  );
}

export function ShipmentTable({ items, onChanged, onError, laserPrinterId, labelPrinterId, empty }: { items: ShipmentDto[]; onChanged: () => void; onError: (e: unknown) => void; laserPrinterId?: string; labelPrinterId?: string; empty?: string }) {
  return (
    <table className="table">
      <thead><tr><th>Nr.</th><th>Fragtfirma</th><th>Kolli</th><th>Fragtbrev / T&T</th><th>Status</th><th>Booket</th><th></th></tr></thead>
      <tbody>
        {items.map((s) => <ShipmentRow key={s.id} s={s} onChanged={onChanged} onError={onError} laserPrinterId={laserPrinterId} labelPrinterId={labelPrinterId} />)}
        {items.length === 0 && <tr><td colSpan={7} className="text-muted">{empty ?? 'Ingen forsendelser.'}</td></tr>}
      </tbody>
    </table>
  );
}

/**
 * Freight for a fulfilment or batch: what the routing rules propose per group of packages, a
 * carrier override, "Book fragt", and the booked shipments with labels and customs invoice.
 */
export function ShippingPanel({ ownerType, ownerId, laserPrinterId, disabled, onBooked, onError }: { ownerType: 'fulfilment' | 'batch'; ownerId: string; laserPrinterId: string; disabled?: boolean; onBooked: () => void; onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [labelPrinterId, setLabelPrinterId] = useLabelPrinter();
  const [overrides, setOverrides] = useState<Record<string, { carrier: CarrierId; service: string }>>({});
  const key = ['shipping-plan', ownerType, ownerId];
  const plan = useQuery({ queryKey: key, queryFn: () => get<ShippingPlanDto>(`/shipping/plan?ownerType=${ownerType}&ownerId=${ownerId}`) });
  const refresh = () => { void qc.invalidateQueries({ queryKey: key }); void qc.invalidateQueries({ queryKey: ['shipments'] }); onBooked(); };
  const book = useMutation({
    mutationFn: () =>
      post<ShippingPlanDto>('/shipping/book', {
        ownerType,
        ownerId,
        labelPrinterId: labelPrinterId || null,
        documentPrinterId: laserPrinterId || null,
        overrides: Object.entries(overrides).map(([packageKind, o]) => ({ packageKind, carrier: o.carrier, service: o.service || null })),
      }),
    onSuccess: (r) => {
      refresh();
      if (!labelPrinterId) {
        const fresh = r.shipments.filter((s) => s.status === 'labelled').slice(0, 3);
        for (const s of fresh) for (const d of s.labelDocumentIds) window.open(`/api/documents/${d}/download`, '_blank');
      }
    },
    onError: (e) => { refresh(); onError(e); },
  });
  const p = plan.data;
  const groups = (p?.groups ?? []).filter((g) => g.packageCount > 0);
  const waiting = (p?.groups ?? []).find((g) => g.packageCount === 0);
  const blocked = groups.some((g) => g.issues.length > 0 && !overrides[g.packageKind]);
  const kinds = [...new Set(groups.map((g) => g.packageKind))];
  return (
    <div className="card space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="label m-0">Fragt</div>
        {p?.customsRequired && <Badge tone="amber">Uden for EU: toldfaktura</Badge>}
        <div className="ml-auto flex items-center gap-2">
          <LabelPrinterSelect value={labelPrinterId} onChange={setLabelPrinterId} />
          {groups.length > 0 && can('shipping.book') && (
            <button className="btn btn-primary" disabled={disabled || book.isPending || groups.every((g) => g.packageCount === 0)} onClick={() => book.mutate()} title={blocked ? 'Ret fejlene først' : ''}>
              {book.isPending ? 'Booker...' : groups.length > 1 ? `Book fragt (${groups.length} forsendelser)` : 'Book fragt'}
            </button>
          )}
        </div>
      </div>
      {groups.length > 0 && (
        <div className="space-y-2">
          {kinds.length > 0 && can('shipping.book') && (
            <div className="flex flex-wrap gap-3 text-sm">
              {kinds.map((k) => {
                const first = groups.find((g) => g.packageKind === k)!;
                const o = overrides[k];
                const carrier = o?.carrier ?? first.carrier ?? '';
                return (
                  <div key={k} className="flex items-center gap-2">
                    <span className="text-muted">{k === 'pallet' ? 'Paller' : 'Pakker'} med</span>
                    <select className="input w-44" value={carrier} onChange={(e) => setOverrides({ ...overrides, [k]: { carrier: e.target.value as CarrierId, service: '' } })}>
                      {!carrier && <option value="">Vælg</option>}
                      {(Object.keys(CARRIER_NAMES) as CarrierId[]).map((c) => <option key={c} value={c}>{CARRIER_NAMES[c]}</option>)}
                    </select>
                    {carrier && (
                      <select className="input w-56" value={o?.service ?? (o ? '' : (first.service ?? ''))} onChange={(e) => setOverrides({ ...overrides, [k]: { carrier: carrier as CarrierId, service: e.target.value } })}>
                        {o && <option value="">Standard for {CARRIER_NAMES[carrier as CarrierId]}</option>}
                        {CARRIER_SERVICES[carrier as CarrierId].map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                      </select>
                    )}
                    {o && <button className="btn" onClick={() => { const n = { ...overrides }; delete n[k]; setOverrides(n); }}>Følg reglen</button>}
                  </div>
                );
              })}
            </div>
          )}
          <table className="table">
            <thead><tr>{ownerType === 'batch' && <th>Ordre</th>}<th>Kolli</th><th>Fragtfirma</th><th>Modtager</th><th>Problemer</th></tr></thead>
            <tbody>
              {groups.slice(0, 50).map((g) => (
                <tr key={g.key} className={g.issues.length ? 'bg-amber-50' : ''}>
                  {ownerType === 'batch' && <td className="whitespace-nowrap">{g.fulfilmentNumber ?? 'Samlet'}</td>}
                  <td className="whitespace-nowrap tabular-nums">{g.packageCount} {g.packageKind === 'pallet' ? 'palle' : 'pk.'} · {kg(g.weightG)}</td>
                  <td className="whitespace-nowrap">{g.carrier ? `${CARRIER_NAMES[g.carrier]} · ${g.service ?? ''}` : '-'}{g.mode && g.mode !== 'live' && <span className="ml-1"><Badge tone="amber">{modeLabel[g.mode]}</Badge></span>}</td>
                  <td className="text-xs">
                    {g.receiver ? [g.receiver.company, g.receiver.name, g.receiver.street1, `${g.receiver.zip ?? ''} ${g.receiver.city ?? ''}`, g.receiver.countryCode].filter(Boolean).join(', ') : '-'}
                    {g.parcelShopId && g.orderId && <ParcelShopLine orderId={g.orderId} parcelShopId={g.parcelShopId} zip={g.receiver?.zip ?? null} street={g.receiver?.street1 ?? null} country={g.receiver?.countryCode ?? null} onChanged={refresh} onError={onError} />}
                  </td>
                  <td className="text-xs text-red-700">{g.issues.join('. ')}</td>
                </tr>
              ))}
              {groups.length > 50 && <tr><td colSpan={5} className="text-muted">... og {groups.length - 50} mere</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {p && groups.length === 0 && waiting && <div className="text-sm text-muted">{waiting.issues.join('. ')}</div>}
      {p && groups.length === 0 && !waiting && p.shipments.length === 0 && <div className="text-sm text-muted">Registrér kolli eller paller, så foreslår systemet fragten.</div>}
      {p && p.shipments.length > 0 && <ShipmentTable items={p.shipments} onChanged={refresh} onError={onError} laserPrinterId={laserPrinterId} labelPrinterId={labelPrinterId} />}
    </div>
  );
}

/** The GLS parcel shop of an order, with a picker of nearby shops from the receiver's postcode. */
export function ParcelShopLine({ orderId, parcelShopId, zip, street, country, onChanged, onError }: { orderId: string; parcelShopId: string; zip: string | null; street: string | null; country: string | null; onChanged: () => void; onError: (e: unknown) => void }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [searchZip, setSearchZip] = useState(zip ?? '');
  const shop = useQuery({ queryKey: ['parcel-shop', parcelShopId], queryFn: () => get<ParcelShopDto | null>(`/shipping/parcel-shops/${parcelShopId}`), staleTime: 3_600_000, retry: false });
  const nearby = useQuery({ queryKey: ['parcel-shops', searchZip, street, country], queryFn: () => get<ParcelShopDto[]>(`/shipping/parcel-shops?${new URLSearchParams({ zip: searchZip, street: street ?? '', country: country ?? 'DK' })}`), enabled: open && searchZip.length >= 3, retry: false });
  const set = useMutation({ mutationFn: (n: string) => put(`/shipping/orders/${orderId}/parcel-shop`, { parcelShopId: n }), onSuccess: () => { setOpen(false); onChanged(); }, onError });
  const s = shop.data;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="font-medium">Pakkeshop {parcelShopId}</span>
      {s ? <span className="text-muted">{s.name}, {s.street}, {s.zip} {s.city}</span> : shop.isError ? <span className="text-red-700">GLS kender ikke pakkeshoppen</span> : null}
      {can('shipping.book') && <button className="text-primary hover:underline" onClick={() => setOpen(true)}>Skift</button>}
      {open && (
        <Modal title="Vælg pakkeshop" onClose={() => setOpen(false)}>
          <div className="mb-3 flex items-center gap-2 text-sm"><label>Postnummer</label><input className="input w-28" value={searchZip} onChange={(e) => setSearchZip(e.target.value)} /></div>
          {nearby.isLoading ? <Spinner /> : nearby.isError ? <div className="text-sm text-red-700">GLS svarer ikke lige nu.</div> : (
            <ul className="divide-y divide-line text-sm">
              {nearby.data?.map((p) => (
                <li key={p.number} className="flex items-center gap-3 py-2">
                  <div className="flex-1"><div className="font-medium">{p.name} <span className="text-muted">({p.number})</span></div><div className="text-muted">{p.street}, {p.zip} {p.city}{p.distanceM != null ? ` · ${p.distanceM < 1000 ? `${p.distanceM} m` : `${(p.distanceM / 1000).toFixed(1)} km`}` : ''}</div></div>
                  {p.number === parcelShopId ? <Badge tone="green">valgt</Badge> : <button className="btn" disabled={set.isPending} onClick={() => set.mutate(p.number)}>Vælg</button>}
                </li>
              ))}
              {nearby.data?.length === 0 && <li className="py-2 text-muted">Ingen pakkeshops fundet.</li>}
            </ul>
          )}
        </Modal>
      )}
    </div>
  );
}
